import { apiClient } from '@/lib/api'
import type {
  StandardResponse,
  PaginatedStandardResponse,
  MessageResponse,
  PaginationInfo,
} from '@/types/api-response'
import { extractData, extractPaginatedData } from '@/types/api-response'
import type {
  AdminOSImage,
  BulkOSImageResult,
  AdminOSImageFilters,
  OSImageFormValues,
} from '@/types/admin'

/**
 * Admin OS Image Management Service
 *
 * The backend stores an OS image as a `resource_templates` row whose interesting fields live
 * inside three JSON columns (`defaultSpecifications`, `defaultConfiguration`, `metadata`).
 * The admin UI works with a flat `OSImageFormValues` shape instead, so this module owns the
 * translation in both directions — no component should ever build those JSON blobs itself.
 *
 * The most important field is `templateName` inside `defaultConfiguration`: that exact string
 * is what provisioning sends to WiService, so it is patched in place rather than regenerated.
 */

/** Parses a JSON column, tolerating null/blank/corrupt values. */
function parseJsonObject(raw: string | undefined | null): Record<string, unknown> {
  if (!raw) return {}
  try {
    const parsed = JSON.parse(raw)
    return parsed && typeof parsed === 'object' ? (parsed as Record<string, unknown>) : {}
  } catch {
    return {}
  }
}

/**
 * Derives the metadata `osFamily` the catalog mappers use to classify an image
 * (debian / redhat / windows / bsd). Returns undefined when the distro cannot be
 * recognised — in that case any osFamily already stored on the row is kept.
 */
function deriveOsFamily(values: OSImageFormValues): string | undefined {
  const haystack = `${values.name} ${values.templateName}`.toLowerCase()
  switch (values.category) {
    case 'Windows':
      return 'windows'
    case 'BSD':
      return 'bsd'
    case 'Linux':
      if (/ubuntu|debian/.test(haystack)) return 'debian'
      if (/centos|rocky|alma|fedora|rhel|red ?hat/.test(haystack)) return 'redhat'
      return undefined
    default:
      return undefined
  }
}

/**
 * Builds the backend request body from the flat form values, preserving every key of the
 * existing row that the form does not render.
 *
 * @param values - Values captured by the OS image form.
 * @param current - The row being edited; omitted when creating.
 */
function toBackendPayload(values: OSImageFormValues, current?: AdminOSImage) {
  const isWindows = values.category === 'Windows'

  const defaultSpecifications = {
    ...parseJsonObject(current?.defaultSpecifications),
    cpu: values.minimumSpecs.cpu,
    memory: values.minimumSpecs.memory,
    disk: values.minimumSpecs.disk,
    networkInterfaces: parseJsonObject(current?.defaultSpecifications).networkInterfaces ?? 1,
  }

  // Only templateName is authoritative here; the access flags come from the existing row so an
  // edit never silently flips cloudInit/sshEnabled/rdpEnabled on a working image.
  const existingConfiguration = parseJsonObject(current?.defaultConfiguration)
  const defaultConfiguration = {
    cloudInit: !isWindows,
    sshEnabled: !isWindows,
    firewallEnabled: true,
    ...(isWindows ? { rdpEnabled: true } : {}),
    ...existingConfiguration,
    templateName: values.templateName,
  }

  const existingMetadata = parseJsonObject(current?.metadata)
  const osFamily = deriveOsFamily(values)
  const metadata = {
    ...existingMetadata,
    templateName: values.templateName,
    // The catalog derives the customer-facing osType from osFamily; osType here mirrors the
    // admin category (Linux/Windows/BSD/Custom), not the lowercase distro slug.
    osType: values.category,
    ...(osFamily ? { osFamily } : {}),
    osVersion: values.version,
    minimumSpecs: {
      cpu: values.minimumSpecs.cpu,
      memory: values.minimumSpecs.memory,
      disk: values.minimumSpecs.disk,
    },
    // Read by the customer catalog (WindowsLicenseMetadata) to warn before a Windows VM is created.
    // Written for Windows images only; any other category keeps whatever the row already holds.
    // The message is kept even while activation is supported, so switching it off again does not
    // make the operator retype it.
    ...(isWindows
      ? {
          windowsLicense: {
            activationSupported: values.licenseActivationSupported,
            warningMessage: values.licenseWarningMessage.trim(),
          },
        }
      : {}),
  }

  return {
    name: values.name,
    description: values.description,
    category: values.category,
    defaultRegion: current?.defaultRegion ?? 'Default',
    defaultSpecifications: JSON.stringify(defaultSpecifications),
    defaultConfiguration: JSON.stringify(defaultConfiguration),
    estimatedHourlyCostCents: current?.estimatedHourlyCost ?? 0,
    estimatedMonthlyCostCents: current?.estimatedMonthlyCost ?? 0,
    currency: current?.currency ?? 'VND',
    isFeatured: values.isFeatured,
    isActive: values.isActive,
    // sortOrder: removed with the Sort Order field — the backend no longer accepts it.
    iconUrl: values.iconUrl || null,
    tags: current?.tags ?? [],
    metadata: JSON.stringify(metadata),
  }
}

/**
 * Reads the Windows license settings stored under `metadata.windowsLicense`. An image with no such
 * block — every image created before the setting existed — reads as supported, the same default
 * the customer catalog applies.
 */
export function readWindowsLicense(metadata: string | undefined | null): {
  activationSupported: boolean
  warningMessage: string
} {
  const block = parseJsonObject(metadata).windowsLicense as
    | { activationSupported?: unknown; warningMessage?: unknown }
    | undefined

  return {
    activationSupported: block?.activationSupported !== false,
    warningMessage: typeof block?.warningMessage === 'string' ? block.warningMessage : '',
  }
}

/** Turns an API row back into the flat values the form binds to. */
export function toFormValues(image: AdminOSImage): OSImageFormValues {
  const windowsLicense = readWindowsLicense(image.metadata)

  return {
    name: image.name,
    version: image.version ?? '',
    category: (['Linux', 'Windows', 'BSD', 'Custom'].includes(image.category)
      ? image.category
      : 'Custom') as OSImageFormValues['category'],
    description: image.description ?? '',
    iconUrl: image.iconUrl ?? '',
    templateName: image.templateName ?? '',
    minimumSpecs: image.minimumSpecs,
    isActive: image.isActive,
    isFeatured: image.isFeatured,
    // No block means nothing was configured: supported, the same default the customer catalog uses.
    licenseActivationSupported: windowsLicense?.activationSupported !== false,
    licenseWarningMessage:
      typeof windowsLicense?.warningMessage === 'string' ? windowsLicense.warningMessage : '',
  }
}

export const adminOSImageService = {
  /**
   * Get paginated list of OS images with filters
   *
   * @param filters - Filter parameters
   */
  async getAll(
    filters: AdminOSImageFilters
  ): Promise<{ data: AdminOSImage[]; pagination: PaginationInfo }> {
    const params = new URLSearchParams()

    if (filters.search) params.append('search', filters.search)
    if (filters.category && filters.category !== 'All') params.append('category', filters.category)
    if (filters.osType && filters.osType !== 'All') params.append('osType', filters.osType)
    if (filters.status && filters.status !== 'All') {
      params.append('isActive', (filters.status === 'Active').toString())
    }
    if (filters.sortBy) params.append('sortBy', filters.sortBy)
    if (filters.sortOrder) params.append('sortOrder', filters.sortOrder)
    params.append('page', filters.page.toString())
    params.append('limit', filters.pageSize.toString())

    const response = await apiClient.get<PaginatedStandardResponse<AdminOSImage>>(
      `/api/v1/admin/os-images?${params.toString()}`
    )
    return extractPaginatedData(response.data)
  },

  /**
   * Get OS image by ID
   *
   * @param id - OS image ID (UUID)
   */
  async getById(id: string): Promise<AdminOSImage> {
    const response = await apiClient.get<StandardResponse<AdminOSImage>>(
      `/api/v1/admin/os-images/${id}`
    )
    return extractData(response.data)
  },

  /**
   * Create new OS image
   *
   * @param values - Values captured by the OS image form
   */
  async create(values: OSImageFormValues): Promise<AdminOSImage> {
    const response = await apiClient.post<StandardResponse<AdminOSImage>>(
      '/api/v1/admin/os-images',
      toBackendPayload(values)
    )
    return extractData(response.data)
  },

  /**
   * Update OS image
   *
   * @param id - OS image ID (UUID)
   * @param values - Values captured by the OS image form
   * @param current - The row being edited, so untouched JSON keys survive the round-trip
   */
  async update(
    id: string,
    values: OSImageFormValues,
    current?: AdminOSImage
  ): Promise<AdminOSImage> {
    const response = await apiClient.put<StandardResponse<AdminOSImage>>(
      `/api/v1/admin/os-images/${id}`,
      toBackendPayload(values, current)
    )
    return extractData(response.data)
  },

  /**
   * Delete OS image
   *
   * @param id - OS image ID (UUID)
   */
  async delete(id: string): Promise<MessageResponse> {
    const response = await apiClient.delete<MessageResponse>(`/api/v1/admin/os-images/${id}`)
    return response.data
  },

  /**
   * Verify a VM template name exists in WiService.
   *
   * The check is case-sensitive on the backend because WiService resolves template names
   * verbatim — "ubuntu-24.04" is not the same template as "Ubuntu-24.04".
   *
   * @param templateName - VM template name
   */
  async verifyTemplate(templateName: string): Promise<boolean> {
    const response = await apiClient.get<StandardResponse<boolean>>(
      `/api/v1/admin/os-images/verify-template?templateName=${encodeURIComponent(templateName)}`
    )
    return extractData(response.data)
  },

  /**
   * Bulk activate OS images
   *
   * @param ids - Array of OS image IDs
   */
  async bulkActivate(ids: string[]): Promise<BulkOSImageResult> {
    const response = await apiClient.post<StandardResponse<BulkOSImageResult>>(
      '/api/v1/admin/os-images/bulk-activate',
      { ids }
    )
    return extractData(response.data)
  },

  /**
   * Bulk deactivate OS images
   *
   * @param ids - Array of OS image IDs
   */
  async bulkDeactivate(ids: string[]): Promise<BulkOSImageResult> {
    const response = await apiClient.post<StandardResponse<BulkOSImageResult>>(
      '/api/v1/admin/os-images/bulk-deactivate',
      { ids }
    )
    return extractData(response.data)
  },

  /**
   * Bulk delete OS images. Images still referenced by VMs come back in `skippedIds`
   * rather than being deleted.
   *
   * @param ids - Array of OS image IDs
   */
  async bulkDelete(ids: string[]): Promise<BulkOSImageResult> {
    const response = await apiClient.post<StandardResponse<BulkOSImageResult>>(
      '/api/v1/admin/os-images/bulk-delete',
      { ids }
    )
    return extractData(response.data)
  },
}
